/**
 * Timezone Preferences API
 */

import supabase from "../config/supabase";

// Coordinates drive the real sunrise/sunset used for the day/night clock faces.
const AUSTRALIAN_CAPITALS = [
  { name: "Adelaide",  state: "SA",  timezone: "Australia/Adelaide",  latitude: -34.9285, longitude: 138.6007 },
  { name: "Brisbane",  state: "QLD", timezone: "Australia/Brisbane",  latitude: -27.4698, longitude: 153.0251 },
  { name: "Canberra",  state: "ACT", timezone: "Australia/Sydney",    latitude: -35.2809, longitude: 149.1300 },
  { name: "Darwin",    state: "NT",  timezone: "Australia/Darwin",    latitude: -12.4634, longitude: 130.8456 },
  { name: "Hobart",    state: "TAS", timezone: "Australia/Hobart",    latitude: -42.8821, longitude: 147.3272 },
  { name: "Melbourne", state: "VIC", timezone: "Australia/Melbourne", latitude: -37.8136, longitude: 144.9631 },
  { name: "Perth",     state: "WA",  timezone: "Australia/Perth",     latitude: -31.9505, longitude: 115.8605 },
  { name: "Sydney",    state: "NSW", timezone: "Australia/Sydney",    latitude: -33.8688, longitude: 151.2093 },
];

const DEFAULT_CITY_ORDER = [
  "Brisbane",
  "Adelaide",
  "Canberra",
  "Darwin",
  "Hobart",
  "Melbourne",
  "Perth",
  "Sydney",
];

const defaultPreferences = () => ({
  city_order: [...DEFAULT_CITY_ORDER],
  clock_format: "analog",
  custom_cities: [],
});

const CAPITAL_NAMES = new Set(AUSTRALIAN_CAPITALS.map((c) => c.name));
const MAX_CUSTOM_CITIES = 50;

/**
 * A custom city is only usable if it can actually drive a clock face: a valid
 * IANA zone for the time, and real coordinates for the sunrise maths. Anything
 * short of that is dropped rather than allowed to throw mid-render.
 */
const sanitiseCustomCity = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const name = typeof raw.name === "string" ? raw.name.trim() : "";
  const timezone = typeof raw.timezone === "string" ? raw.timezone.trim() : "";
  const latitude = Number(raw.latitude);
  const longitude = Number(raw.longitude);

  if (!name || !timezone) return null;
  if (!Number.isFinite(latitude) || Math.abs(latitude) > 90) return null;
  if (!Number.isFinite(longitude) || Math.abs(longitude) > 180) return null;

  // A capital's name is a key elsewhere in the Clocks tab, so a custom city may
  // not claim one.
  if (CAPITAL_NAMES.has(name)) return null;

  try {
    // Throws RangeError on an unknown zone, which is the only reliable check.
    new Intl.DateTimeFormat("en-US", { timeZone: timezone });
  } catch {
    return null;
  }

  return {
    name,
    state: typeof raw.state === "string" ? raw.state.trim().slice(0, 6) : "",
    timezone,
    latitude,
    longitude,
    placeId: raw.placeId ? String(raw.placeId) : null,
  };
};

const sanitiseCustomCities = (raw) => {
  if (!Array.isArray(raw)) return [];

  const seen = new Set();
  const cities = [];

  raw.forEach((entry) => {
    const city = sanitiseCustomCity(entry);
    if (!city || seen.has(city.name)) return;
    seen.add(city.name);
    cities.push(city);
  });

  return cities.slice(0, MAX_CUSTOM_CITIES);
};

const timezonePreferencesApi = {
  /**
   * Get user's timezone preferences.
   *
   * Always resolves with usable preferences. Clocks are a read-only convenience,
   * so a signed-out user, a missing row, or an unmigrated table all fall back to
   * defaults rather than leaving the caller with nothing to render.
   */
  async get() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return { success: true, data: defaultPreferences(), persisted: false };

      const { data, error } = await supabase
        .from("user_timezone_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error || !data) {
        return { success: true, data: defaultPreferences(), persisted: false };
      }

      return {
        success: true,
        persisted: true,
        data: {
          city_order: Array.isArray(data.city_order) && data.city_order.length
            ? data.city_order
            : [...DEFAULT_CITY_ORDER],
          clock_format: data.clock_format === "digital" ? "digital" : "analog",
          // Absent on a database that hasn't taken migration 022 yet.
          custom_cities: sanitiseCustomCities(data.custom_cities),
        },
      };
    } catch {
      return { success: true, data: defaultPreferences(), persisted: false };
    }
  },

  /**
   * Update timezone preferences (or create if doesn't exist).
   *
   * Persistence is best-effort: a failure here must not stop the clocks from
   * working for the rest of the session.
   */
  async update({ city_order, clock_format, custom_cities }) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return { success: false, error: "Not authenticated" };

      const payload = { user_id: user.id };
      if (city_order) payload.city_order = city_order;
      if (clock_format) payload.clock_format = clock_format;
      if (custom_cities) payload.custom_cities = sanitiseCustomCities(custom_cities);

      const { data, error } = await supabase
        .from("user_timezone_preferences")
        .upsert(payload, { onConflict: "user_id" })
        .select()
        .maybeSingle();

      if (error) return { success: false, error: error.message };
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err?.message || "Failed to save preferences" };
    }
  },

  /**
   * Get timezone info for Australian capitals
   */
  getAustralianCapitals() {
    return AUSTRALIAN_CAPITALS;
  },

  /**
   * The Clocks tab keys cities by name, and place names are not unique — a
   * search for Detroit turns up six of them. Qualify a clashing name with its
   * region so both can sit on the tab at once.
   */
  makeUniqueCityName(desired, takenNames = [], qualifier = "") {
    const taken = new Set([...CAPITAL_NAMES, ...takenNames]);
    const base = (desired || "").trim() || "Unnamed";
    if (!taken.has(base)) return base;

    const qualified = qualifier ? `${base} (${qualifier})` : base;
    if (qualifier && !taken.has(qualified)) return qualified;

    for (let n = 2; n <= 99; n += 1) {
      const candidate = `${qualified} ${n}`;
      if (!taken.has(candidate)) return candidate;
    }
    return `${qualified} ${Date.now()}`;
  },

  /**
   * Build the default ordering: the user's own capital first, the rest alphabetical.
   */
  buildCityOrder(userCity = "Brisbane") {
    const names = AUSTRALIAN_CAPITALS.map((c) => c.name);
    const home = names.includes(userCity) ? userCity : "Brisbane";
    return [home, ...names.filter((name) => name !== home).sort()];
  },

  /**
   * Map user's location to nearest capital city
   */
  mapLocationToCapital(location) {
    if (!location) return "Brisbane"; // Default

    const locationUpper = location.toUpperCase();

    // Queensland → Brisbane
    if (locationUpper.includes("QLD") || locationUpper.includes("QUEENSLAND")) {
      return "Brisbane";
    }

    // NSW → Sydney
    if (locationUpper.includes("NSW") || locationUpper.includes("NEW SOUTH WALES")) {
      return "Sydney";
    }

    // Victoria → Melbourne
    if (locationUpper.includes("VIC") || locationUpper.includes("VICTORIA")) {
      return "Melbourne";
    }

    // South Australia → Adelaide
    if (locationUpper.includes("SA") || locationUpper.includes("SOUTH AUSTRALIA")) {
      return "Adelaide";
    }

    // Western Australia → Perth
    if (locationUpper.includes("WA") || locationUpper.includes("WESTERN AUSTRALIA")) {
      return "Perth";
    }

    // Tasmania → Hobart
    if (locationUpper.includes("TAS") || locationUpper.includes("TASMANIA")) {
      return "Hobart";
    }

    // Northern Territory → Darwin
    if (locationUpper.includes("NT") || locationUpper.includes("NORTHERN TERRITORY")) {
      return "Darwin";
    }

    // ACT → Canberra
    if (locationUpper.includes("ACT") || locationUpper.includes("AUSTRALIAN CAPITAL TERRITORY") || locationUpper.includes("CANBERRA")) {
      return "Canberra";
    }

    // Default to Brisbane if can't determine
    return "Brisbane";
  },
};

export default timezonePreferencesApi;
